import { useCallback, useEffect, useLayoutEffect, type RefObject, type TransitionEvent } from 'react';
import {
    buildSkillPanelFragmentScrollReadiness,
    isSkillPanelWidthTransitionEnd,
    registerSkillPanelHashChangeScroll,
    tryScrollSkillPanelToFragment,
} from './skillPanelScroll';

export type UseSkillPanelFragmentScrollArgs = {
    locationHash: string;
    skillId: string | null;
    skillHtml: string | undefined;
    isCollapsed: boolean;
    loading: boolean;
    isEditing: boolean;
    hasSkill: boolean;
    scrollParentRef: RefObject<HTMLElement | null>;
    /** False when embedded in a host page; see SkillPanelFragmentScrollReadiness. */
    ownsDocumentScroll?: boolean;
};

export type UseSkillPanelFragmentScrollResult = {
    /**
     * Attach to the panel root (the element with the width transition). When the panel
     * finishes expanding, the guide's layout is final — re-scroll to the URL fragment,
     * since a scroll during the transition computed offsets against mid-transition
     * line wrapping (e.g. a section link that reopened the panel).
     */
    onPanelTransitionEnd: (event: TransitionEvent<HTMLElement>) => void;
};

/**
 * Keeps skill-panel fragment scrolling in sync with React Router (`location.hash`) and with
 * same-document `hashchange` updates. Scroll math lives in `skillPanelScroll.ts`.
 */
export function useSkillPanelFragmentScroll({
    locationHash,
    skillId,
    skillHtml,
    isCollapsed,
    loading,
    isEditing,
    hasSkill,
    scrollParentRef,
    ownsDocumentScroll = true,
}: UseSkillPanelFragmentScrollArgs): UseSkillPanelFragmentScrollResult {
    useLayoutEffect(() => {
        tryScrollSkillPanelToFragment(
            locationHash,
            buildSkillPanelFragmentScrollReadiness({
                isCollapsed,
                loading,
                hasSkill,
                isEditing,
                scrollParent: scrollParentRef.current,
                ownsDocumentScroll,
            }),
        );
    }, [locationHash, skillId, skillHtml, isCollapsed, loading, isEditing, ownsDocumentScroll]);

    useEffect(() => {
        return registerSkillPanelHashChangeScroll(() =>
            buildSkillPanelFragmentScrollReadiness({
                isCollapsed,
                loading,
                hasSkill,
                isEditing,
                scrollParent: scrollParentRef.current,
                ownsDocumentScroll,
            }),
        );
    }, [isCollapsed, loading, skillHtml, isEditing]);

    const onPanelTransitionEnd = useCallback(
        (event: TransitionEvent<HTMLElement>) => {
            if (!isSkillPanelWidthTransitionEnd(event)) {
                return;
            }
            tryScrollSkillPanelToFragment(
                locationHash,
                buildSkillPanelFragmentScrollReadiness({
                    isCollapsed,
                    loading,
                    hasSkill,
                    isEditing,
                    scrollParent: scrollParentRef.current,
                    ownsDocumentScroll,
                }),
            );
        },
        [locationHash, isCollapsed, loading, hasSkill, isEditing, ownsDocumentScroll],
    );

    return { onPanelTransitionEnd };
}
